import { useEffect, useState } from "react";
import { ClipboardList } from "lucide-react";
import { ClockIcon, CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { fetchMyPanels, fetchJuryIdeas, fetchIdeaSelections } from "../../api/jury";

interface PanelMember { _id: string; name: string; email: string; }
interface Panel {
  _id: string;
  name: string;
  scope: "idea_selection" | "round";
  roundType?: string | null;
  members: PanelMember[];
  hackathonId: {
    _id: string;
    title: string;
    phases: { submission: { ideaSelection: { decisionDeadline: string } }; evaluation: { rounds: Array<{ _id: string; name: string; startDate: string; endDate: string }> } };
  };
}

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const daysLeft = (d: string) =>
  Math.max(0, Math.ceil((new Date(d).getTime() - Date.now()) / 86400000));

const initials = (name: string) =>
  name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

const getHackathonId = (hackathonId: any): string => {
  if (typeof hackathonId === "string") return hackathonId;
  return hackathonId?._id ?? "";
};

const getHackathonTitle = (hackathonId: any): string => {
  if (typeof hackathonId === "string") return "Unknown Hackathon";
  return hackathonId?.title ?? "Unknown Hackathon";
};

const getDeadline = (hackathonId: any): string | undefined => {
  if (typeof hackathonId === "string") return undefined;
  return hackathonId?.phases?.submission?.ideaSelection?.decisionDeadline;
};

export default function JuryOverview() {
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [panels, setPanels] = useState<Panel[]>([]);
  const [notAssigned, setNotAssigned] = useState(false);
  const [pending, setPending] = useState(0);
  const [selected, setSelected] = useState(0);
  const [rejected, setRejected] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!token) {
      console.log("No token available");
      return;
    }
    setLoading(true);
    fetchMyPanels(token)
      .then(async (ps: Panel[]) => {
        setPanels(ps);
        // Aggregate stats across all idea_selection panels
        const ideaSelectionPanels = ps.filter((p) => p.scope === "idea_selection");
        if (ideaSelectionPanels.length > 0) {
          const hackathonId = ideaSelectionPanels[0].hackathonId._id;
          const [ideas, selections] = await Promise.all([
            fetchJuryIdeas(token, hackathonId),
            fetchIdeaSelections(token, hackathonId),
          ]);
          const decidedIds = new Set(selections.map((s: any) => String(s.ideaId?._id ?? s.ideaId)));
          setPending(ideas.filter((i: any) => !decidedIds.has(String(i._id))).length);
          setSelected(selections.filter((s: any) => s.decision === "selected").length);
          setRejected(selections.filter((s: any) => s.decision === "rejected").length);
        }
      })
      .catch((err: any) => {
        console.error("Error fetching panel:", err);
        setError(err.message || "Failed to fetch panel assignment");
        setNotAssigned(true);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const ideaSelectionPanels = panels.filter((p) => p.scope === "idea_selection");
  const roundPanels = panels.filter((p) => p.scope === "round");
  const deadline = ideaSelectionPanels[0]?.hackathonId?.phases?.submission?.ideaSelection?.decisionDeadline;

  const stats = [
    { icon: ClockIcon,        bg: "bg-[rgba(255,159,67,0.06)]",  color: "text-[#ff9f43]", label: "Pending Reviews", value: loading ? "—" : String(pending)  },
    { icon: CheckCircleIcon,  bg: "bg-[rgba(0,214,143,0.06)]",   color: "text-[#00d68f]", label: "Selected",        value: loading ? "—" : String(selected) },
    { icon: XCircleIcon,      bg: "bg-[rgba(255,99,99,0.06)]",   color: "text-[#ff6363]", label: "Rejected",        value: loading ? "—" : String(rejected) },
  ];

  return (
    <div className="p-3 sm:p-6 md:p-8 space-y-4 sm:space-y-5 md:space-y-6">
      {/* WELCOME */}
      <div className="bg-gradient-to-br from-[rgba(240,245,255,0.9)] to-[rgba(250,251,255,0.7)] rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 border border-[rgba(228,232,242,0.5)] shadow-xl">
        <p className="text-xs font-medium text-[#6e7891] uppercase tracking-wider mb-2">
          {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
        </p>
        <h1 className="text-xl sm:text-2xl md:text-3xl mb-2">
          <span className="text-[#1a2332]">Welcome, </span>
          <span className="text-[#0b9fd9] font-medium break-words">{user?.name || "Jury Member"}</span>
        </h1>
        <p className="text-sm sm:text-base text-[#6e7891]">Review and make final decisions on submitted ideas.</p>
      </div>

      {/* NOT ASSIGNED STATE */}
      {notAssigned && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center">
          <p className="text-sm font-medium text-amber-800">You are not assigned to any panel yet.</p>
          <p className="text-xs sm:text-sm text-amber-600 mt-1">Please contact the admin to be added to an idea selection or evaluation panel.</p>
        </div>
      )}

      {/* STATS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
        {stats.map(({ icon: Icon, bg, color, label, value }) => (
          <div key={label} className="bg-gradient-to-br from-white/90 to-white/60 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 border border-[rgba(228,236,244,0.4)] shadow-lg">
            <div className={`w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-xl ${bg} flex items-center justify-center mb-4 sm:mb-5 md:mb-6 shadow-md`}>
              <Icon className={`h-5 w-5 sm:h-5.5 sm:w-5.5 md:h-6 md:w-6 ${color}`} />
            </div>
            <p className="text-xs font-medium text-[#8b93a8] uppercase tracking-wider mb-1 sm:mb-2">{label}</p>
            <p className={`text-2xl sm:text-3xl md:text-3xl font-semibold ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* IDEA SELECTION PANELS */}
      {!loading && ideaSelectionPanels.filter((panel) => panel.hackathonId != null).map((panel) => (
        <div key={panel._id} className="bg-white rounded-xl sm:rounded-xl border border-[#e4e8ee] shadow-sm overflow-hidden">
          <div className="px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b border-[#e4e8ee]">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-medium text-[#0058ab] uppercase tracking-wide">Idea Selection Panel</span>
            </div>
            <p className="text-xs text-[#8b93a8]">Assigned Hackathon</p>
            <h2 className="text-sm sm:text-base font-semibold text-[#1a2332] mt-0.5 break-words">{panel.hackathonId.title}</h2>
            <div className="mt-3 flex flex-col sm:flex-row gap-4 sm:gap-8 text-xs sm:text-sm">
              <div>
                <p className="text-xs text-[#8b93a8]">Panel</p>
                <p className="font-medium text-[#1a2332] break-words">{panel.name}</p>
              </div>
              {deadline && (
                <div>
                  <p className="text-xs text-[#8b93a8]">Decision Deadline</p>
                  <p className="font-medium text-[#ff9f43]">
                    {fmtDate(deadline)} · {daysLeft(deadline)} days left
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="px-4 sm:px-5 md:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs sm:text-sm font-semibold text-[#1a2332]">Panel Members</p>
              <span className="text-xs text-[#8b93a8]">{panel.members.length} members</span>
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-4">
              {panel.members.map((m) => (
                <div key={m._id} className="flex items-center gap-2">
                  <div className="flex h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-semibold text-blue-700">
                    {initials(m.name)}
                  </div>
                  <span className="text-xs sm:text-xs text-[#4a5068] truncate">{m.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}

      {/* ROUND PANELS */}
      {!loading && roundPanels.filter((panel) => panel.hackathonId != null).map((panel) => {
        const rounds = panel.hackathonId?.phases?.evaluation?.rounds ?? [];
        return (
          <div key={panel._id} className="bg-white rounded-xl sm:rounded-xl border border-[#e4e8ee] shadow-sm overflow-hidden">
            <div className="px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b border-[#e4e8ee]">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-[#008282] uppercase tracking-wide">
                  Evaluation Panel{panel.roundType ? ` · ${panel.roundType}` : ""}
                </span>
              </div>
              <p className="text-xs text-[#8b93a8]">Assigned Hackathon</p>
              <h2 className="text-sm sm:text-base font-semibold text-[#1a2332] mt-0.5 break-words">{panel.hackathonId.title}</h2>
              <div className="mt-3 flex flex-col sm:flex-row gap-4 sm:gap-8 text-xs sm:text-sm">
                <div>
                  <p className="text-xs text-[#8b93a8]">Panel</p>
                  <p className="font-medium text-[#1a2332] break-words">{panel.name}</p>
                </div>
                {rounds.length > 0 && (
                  <div>
                    <p className="text-xs text-[#8b93a8]">Rounds</p>
                    <p className="font-medium text-[#1a2332] break-words">{rounds.filter((r) => r != null).map((r) => r.name).join(", ")}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="px-4 sm:px-5 md:px-6 py-3 sm:py-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs sm:text-sm font-semibold text-[#1a2332]">Panel Members</p>
                <span className="text-xs text-[#8b93a8]">{panel.members.length} members</span>
              </div>
              <div className="flex flex-wrap gap-2 sm:gap-4">
                {panel.members.map((m) => (
                  <div key={m._id} className="flex items-center gap-2">
                    <div className="flex h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 items-center justify-center rounded-full bg-teal-100 text-xs font-semibold text-teal-700">
                      {initials(m.name)}
                    </div>
                    <span className="text-xs sm:text-xs text-[#4a5068] truncate">{m.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}

      {/* CTA */}
      <button
        onClick={() => navigate("/jury-dashboard/review-queue")}
        disabled={notAssigned}
        className="w-full sm:w-auto flex items-center justify-center gap-2 rounded bg-[#0058ab] px-4 sm:px-6 py-2.5 sm:py-3 text-sm font-medium text-white hover:bg-[#003d7a] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <ClipboardList className="h-4 w-4" />
        Go to Review Queue
      </button>
    </div>
  );
}
